import { createSelector } from '@reduxjs/toolkit';
import type { TypedUseQueryStateResult } from '@reduxjs/toolkit/dist/query/react';
import { BaseQueryFn } from '@reduxjs/toolkit/query';

import { sortGroupKeyFunc } from '../../../../shared/lib';
import { FormattedTasks, TaskCounts, TaskInListDto } from '../../../../shared/model/tasks/types';
import { SortDirection, SortingTask, TaskGroups } from '../config';
import { getTask, getTaskGroupKey, getTaskGroupSettings, isDayOrMonthGroup } from '../lib/data-formatting';

type TaskResult = TypedUseQueryStateResult<TaskInListDto[], unknown, BaseQueryFn>;

export const selectFilteredTasks = createSelector(
  (res: TaskResult) => res.data,
  (res: TaskResult, settings: { sort: SortingTask; groupId?: string }) => settings,
  (data, settings) => {
    return getAllFormattedTasks(data, settings) ?? [];
  }
);

export const getInitCounts = (): TaskCounts => ({
  allTaskCounter: 0,
  viewedTaskCounter: 0,
  newTaskCounter: 0,
});

export const getAllFormattedTasks = (
  data: TaskInListDto[] | undefined,
  settings: {
    groupId?: string;
    sort: SortingTask;
  }
): FormattedTasks => {
  const newData: FormattedTasks = {
    countsMap: {
      [TaskGroups.None]: {
        allTaskCounter: 0,
        viewedTaskCounter: 0,
        newTaskCounter: 0,
      },
    },
    groupKeys: [],
    taskListsMap: {
      [TaskGroups.None]: [],
    },
  };

  if (!data) {
    return newData;
  }

  const { property, dateLength } = getTaskGroupSettings(settings.groupId);
  const years = new Set<string>();
  const currentYear = new Date().getFullYear().toString();
  const needDivider = isDayOrMonthGroup(settings.groupId);
  const direction = settings.sort[1];

  newData.countsMap[TaskGroups.None].allTaskCounter = data.length;
  data.forEach((task) => {
    // все задачи
    newData.taskListsMap[TaskGroups.None].push(getTask(task));

    if (!task.task_viewed) {
      newData.countsMap[TaskGroups.None].newTaskCounter += 1;
    } else {
      newData.countsMap[TaskGroups.None].viewedTaskCounter += 1;
    }

    if (!property || !settings.groupId || settings.groupId === TaskGroups.None) {
      return;
    }

    // сгруппированные задачи, key - значения полей, если поле, по которому происходит группировка, дата, то ее часть (YYYY / YYYY-MM / YYYY-MM-DD)
    const key = getTaskGroupKey(task, property, dateLength);
    if (needDivider && (direction === SortDirection.Asc || !key.startsWith(currentYear))) {
      years.add(key.slice(0, 4));
    }

    if (!newData.taskListsMap[key]) {
      newData.taskListsMap[key] = [];
      newData.countsMap[key] = {
        allTaskCounter: 0,
        viewedTaskCounter: 0,
        newTaskCounter: 0,
      };
    }

    newData.taskListsMap[key].push(getTask(task));

    if (!task.task_viewed) {
      newData.countsMap[key].newTaskCounter += 1;
    } else {
      newData.countsMap[key].viewedTaskCounter += 1;
    }
    newData.countsMap[key].allTaskCounter += 1;
  });

  if (settings.groupId !== TaskGroups.None) {
    newData.groupKeys = Object.keys(newData.taskListsMap)
      .filter((value) => value !== TaskGroups.None)
      .concat(Array.from(years))
      .sort(sortGroupKeyFunc(direction, isDayOrMonthGroup(settings.groupId)));

    newData.groupKeys.unshift(TaskGroups.None);
  }

  return newData;
};
